import { endPoll } from '@api-client/api-client';
import type { PollRead } from '@models/api/poll';
import { POLL_ERROR_CODES } from '@lib/polls/pollErrors';
import { usePollBlockWrite, type PollBlockWriteVariables } from './usePollBlockWrite';

export interface UsePollEndOptions {
  questionId: string;
  slug: string | null;
  isAuthor: boolean;
}

/**
 * The card once ended, until the server's block comes back (B3): closed now, nothing left to end
 * or edit, Reopen for its author. Only when the counts are already on screen: for a platform admin
 * who never saw them a guess would draw "No one answered", so the card waits for the server.
 */
export const predictPollAfterEnd = (
  poll: PollRead,
  isAuthor: boolean,
  nowIso: string,
): PollRead | null => {
  if (poll.voters_count === null) return null;
  return {
    ...poll,
    is_closed: true,
    closed_at: nowIso,
    results_visible: true,
    can_end: false,
    can_edit: false,
    can_reopen: isAuthor && poll.mode === 'standard',
  };
};

/**
 * "End poll now" (TECH-197 § Time and ending; CONTRACT § 4.4): its author, or a platform admin.
 * 409 `POLL_CLOSED` (it ended by time, or someone else ended it) re-reads the poll; no toast.
 */
export const usePollEnd = ({ questionId, slug, isAuthor }: UsePollEndOptions) => {
  const { mutate, isPending } = usePollBlockWrite<PollBlockWriteVariables>({
    questionId,
    slug,
    send: async id => (await endPoll(id)).data,
    predict: current => predictPollAfterEnd(current, isAuthor, new Date().toISOString()),
    staleCode: POLL_ERROR_CODES.closed,
  });
  return {
    end: (base: PollRead) => {
      mutate({ base });
    },
    isPending,
  };
};
